import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Box, SteppedProcess } from '@ally/metronome-ui'
import { StepOneAddNewBene } from './StepOneAddNewBene'
import {
  StepTwoAddNewBene,
  StepTwoFormValues,
  stepTwoFormInitialValues,
} from './StepTwoAddNewBene'
import {
  BENEFICIARY_CLASSIFICATION,
  RELATIONSHIP_OPTIONS,
  RELATIONSHIP_OPTIONS_NO_SPOUSE,
} from '../../../utils/constants'
import { Beneficiary, BeneficiaryClassification, Option } from '../../../types'
import {
  StepThreeAddNewBeneNonIra,
  StepThreeFormValues,
} from '../../NonIraBeneficiaries/AddBeneficiaryFlow/StepThreeAddNewBeneNonIra'
import {
  AllocationStepIra,
  AllocationStepIraValues,
  allocationStepIraInitialValues,
} from '../../IraBeneficiaries/AddBeneficiaryFlow/AllocationStepIra'
import { VerifyStep } from '../../IraBeneficiaries/AddBeneficiaryFlow/VerifyStep'
import { maskSSN, formatDOBforInput } from '../../../utils/formatter'
import { useFeatureFlags } from '../../../hooks/use-feature-flags'
import { Flags } from '../../../utils/feature-flags'

const SteppedProcessForm: React.FC<{
  isIraPage: boolean
  existingBenes?: Beneficiary[]
  distributionType?: string
  accountId: string
  creatingExistingBene?: boolean
  beneficiarySelection?: Beneficiary
  setDisplayOtherFdicModalContent?: React.Dispatch<
    React.SetStateAction<boolean>
  >
  classificationToOther?: boolean
  isMarried?: string
}> = ({
  isIraPage,
  existingBenes,
  distributionType,
  accountId,
  creatingExistingBene,
  beneficiarySelection,
  setDisplayOtherFdicModalContent,
  classificationToOther,
  isMarried,
}) => {
  const ffBeneficiariesCommunityPropertyFix = useFeatureFlags(
    Flags.BeneficiariesCommunityPropertyFix,
  )

  const ffSsnTaxIdUiMaskingEnabled = useFeatureFlags(
    Flags.SsnTaxIdUiMaskingEnabled,
  )
  // istanbul ignore next - optional chaining
  const hasPrimaryBene = existingBenes?.filter(
    bene => bene.beneficiaryType === 'Primary',
  ).length
  const {
    firstName,
    middleName,
    lastName,
    dateOfBirth,
    relationshipToOwner,
    ssn,
    beneficiaryType,
  } = beneficiarySelection || {}

  const existingRelationshipToOwner = useMemo(() => {
    // istanbul ignore next - This is temporary logic. Once finalize the flow remove the flag and update test case
    const getRelationshipOptions = (): Option[] => {
      if (isMarried === 'No') {
        return RELATIONSHIP_OPTIONS_NO_SPOUSE
      }
      if (
        isMarried === 'Yes' &&
        !ffBeneficiariesCommunityPropertyFix &&
        hasPrimaryBene
      ) {
        return RELATIONSHIP_OPTIONS_NO_SPOUSE
      }
      return RELATIONSHIP_OPTIONS
    }

    return getRelationshipOptions().find(el => el.label === relationshipToOwner)
      ?.value
  }, [
    ffBeneficiariesCommunityPropertyFix,
    hasPrimaryBene,
    isMarried,
    relationshipToOwner,
  ])

  const [beneficiaryClassification, setBeneficiaryClassification] = useState('')
  const stepTwoInitialValuesExistingBenes = {
    ...stepTwoFormInitialValues,
    firstName: firstName || '',
    middleName: middleName || '',
    lastName: lastName || '',
    dateOfBirth: formatDOBforInput(dateOfBirth),
    relationshipToOwner: existingRelationshipToOwner || '',
    ...(isIraPage && !ffSsnTaxIdUiMaskingEnabled
      ? {
          maskedSsn: '',
          identificationNumber: '',
        }
      : {
          maskedSsn: ssn
            ? // istanbul ignore next - already have test for it
              maskSSN(ssn)
            : '',
          identificationNumber: ssn || '',
        }),
    beneficiaryType: beneficiaryType?.toLowerCase() || '',
    isMarried: isMarried === 'Yes',
  }
  const stepTwoInitialValuesNewBene = {
    ...stepTwoFormInitialValues,
    isMarried: isMarried === 'Yes',
  }
  const [stepTwoFormValues, setStepTwoFormValues] = useState<StepTwoFormValues>(
    creatingExistingBene
      ? stepTwoInitialValuesExistingBenes
      : stepTwoInitialValuesNewBene,
  )
  const [errorMessage, setErrorMessage] = useState('')
  const [stepTwoFormErrorCode, setStepTwoFormErrorCode] = useState('')
  const [stepThreeFormValues, setStepThreeFormValues] =
    useState<StepThreeFormValues>({})

  const newBenePercentage =
    !existingBenes || !existingBenes.length ? '100' : '0'

  const [allocationStepIraValues, setAllocationStepIraValues] =
    useState<AllocationStepIraValues>(
      allocationStepIraInitialValues(existingBenes, newBenePercentage),
    )

  const updateStateStepTwo = (
    valuesToUpdate: Partial<StepTwoFormValues>,
  ): void => {
    setStepTwoFormValues(
      // istanbul ignore next // low value
      prevState => ({
        ...prevState,
        ...valuesToUpdate,
      }),
    )
  }

  // istanbul ignore next optional chaining
  const isEqualDistribution = existingBenes?.find(
    bene => bene.allocationPercentage === 'Equal',
  )

  const stepThreeFormInitialValues: StepThreeFormValues =
    existingBenes && existingBenes.length
      ? existingBenes.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.beneficiaryId]:
              curr.allocationPercentage && curr.allocationPercentage !== 'Equal'
                ? // istanbul ignore next
                  parseInt(curr.allocationPercentage, 10).toString()
                : '0',
          }),
          {
            newBeneficiaryFundAllocation: '0',
          },
        )
      : {
          newBeneficiaryFundAllocation: newBenePercentage,
        }

  const setStepThreeInitialValues = (): void => {
    setStepThreeFormValues({
      ...stepThreeFormInitialValues,
      fundAllocationType:
        isEqualDistribution || (existingBenes && existingBenes.length === 1)
          ? 'equal'
          : 'specific',
    })
  }

  useEffect(() => {
    setStepThreeInitialValues()
  }, [])

  const nonIraPageProcess = [
    {
      id: 'beneficiary-classification',
      title: { text: 'Beneficiary classification' },
      content: (
        <StepOneAddNewBene
          existingBeneficiaryClassification={beneficiaryClassification}
          setBeneficiaryClassification={setBeneficiaryClassification}
          updateStateStepTwo={updateStateStepTwo}
          updateStateStepThree={setStepThreeInitialValues}
          setStepTwoFormErrorCode={setStepTwoFormErrorCode}
          setDisplayOtherFdicModalContent={setDisplayOtherFdicModalContent}
          classificationToOther={classificationToOther}
        />
      ),
      summary: {
        items: [
          {
            text: `${
              BENEFICIARY_CLASSIFICATION[
                beneficiaryClassification as BeneficiaryClassification
              ]
            }`,
            ariaLabel: `Step 1 Summary: ${
              BENEFICIARY_CLASSIFICATION[
                beneficiaryClassification as BeneficiaryClassification
              ]
            }`,
          },
        ],
      },
    },
    {
      id: 'beneficiary-information',
      title: { text: 'Beneficiary information' },
      content: (
        <StepTwoAddNewBene
          beneficiaryClassification={beneficiaryClassification}
          isIraPage={isIraPage}
          hasExistingBenes={(existingBenes && !!existingBenes.length) || false}
          updateStateStepTwo={updateStateStepTwo}
          stepTwoFormValues={stepTwoFormValues}
          stepTwoFormErrorCode={stepTwoFormErrorCode}
          setStepTwoFormErrorCode={setStepTwoFormErrorCode}
        />
      ),
    },
    {
      id: 'allocation',
      title: { text: 'Allocation' },
      content: (
        <StepThreeAddNewBeneNonIra
          existingBenes={existingBenes}
          beneficiaryClassification={beneficiaryClassification}
          stepTwoFormValues={stepTwoFormValues}
          distributionType={distributionType}
          accountId={accountId}
          setErrorMessage={setErrorMessage}
          setStepTwoFormErrorCode={setStepTwoFormErrorCode}
          stepThreeFormValues={stepThreeFormValues}
          setStepThreeFormValues={setStepThreeFormValues}
        />
      ),
    },
  ]

  // istanbul ignore next // low value
  const filteredIraBenes = existingBenes?.filter(
    bene =>
      bene.beneficiaryType?.toLowerCase() ===
      stepTwoFormValues.beneficiaryType?.toLowerCase(),
  )

  const iraPageProcess = [
    {
      id: 'beneficiary-information',
      title: { text: 'Beneficiary information' },
      content: (
        <StepTwoAddNewBene
          beneficiaryClassification={beneficiaryClassification}
          isIraPage={isIraPage}
          hasExistingBenes={(existingBenes && !!existingBenes.length) || false}
          updateStateStepTwo={updateStateStepTwo}
          stepTwoFormValues={stepTwoFormValues}
          stepTwoFormErrorCode={stepTwoFormErrorCode}
          setStepTwoFormErrorCode={setStepTwoFormErrorCode}
          isMarried={isMarried}
          hasPrimaryBene={hasPrimaryBene}
        />
      ),
    },
    {
      id: 'allocation',
      title: { text: 'Allocation' },
      content: (
        <AllocationStepIra
          filteredIraBenes={filteredIraBenes}
          allocationStepIraValues={allocationStepIraValues}
          setAllocationStepIraValues={setAllocationStepIraValues}
        />
      ),
      summary: {
        items: [],
        editStepButton: {
          isHidden: !filteredIraBenes,
        },
      },
    },
    {
      id: 'verify',
      title: { text: 'Verify beneficiary' },
      content: (
        <VerifyStep
          stepTwoFormValues={stepTwoFormValues}
          allocationStepIraValues={allocationStepIraValues}
          existingBenes={existingBenes}
          accountId={accountId}
          setStepTwoFormErrorCode={setStepTwoFormErrorCode}
          setErrorMessage={setErrorMessage}
        />
      ),
    },
  ]

  return (
    <>
      {errorMessage && (
        /* istanbul ignore next */ <Box mb="md">
          <Alert
            variant="error"
            focusOnMount
            disableContainer
            body={errorMessage}
          />
        </Box>
      )}
      <SteppedProcess
        variant="vertical"
        steps={isIraPage ? iraPageProcess : nonIraPageProcess}
        a11y={{
          ariaLabelSection: 'setup your account in three easy steps',
        }}
      />
    </>
  )
}

export const AddNewBene: React.FC<{
  isIraPage: boolean
  existingBenes?: Beneficiary[]
  distributionType?: string
  accountId: string
  creatingExistingBene?: boolean
  beneficiarySelection?: Beneficiary
  setDisplayOtherFdicModalContent?: React.Dispatch<
    React.SetStateAction<boolean>
  >
  classificationToOther?: boolean
  isMarried?: string
}> = ({
  isIraPage,
  existingBenes,
  distributionType,
  accountId,
  creatingExistingBene,
  beneficiarySelection,
  setDisplayOtherFdicModalContent,
  classificationToOther,
  isMarried,
}) => {
  return (
    <SteppedProcessForm
      isIraPage={isIraPage}
      existingBenes={existingBenes}
      distributionType={distributionType}
      accountId={accountId}
      creatingExistingBene={creatingExistingBene}
      beneficiarySelection={beneficiarySelection}
      setDisplayOtherFdicModalContent={setDisplayOtherFdicModalContent}
      classificationToOther={classificationToOther}
      isMarried={isMarried}
    />
  )
}
